import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "../lib/auth.ts";
import { useOrg } from "../lib/useOrg.ts";
import { api, type Asset } from "../lib/api.ts";

// Reseller portal (COMMISSIONING_WARRANTY.md P5.2). Reseller-type orgs (incl. the maker)
// see the stock they hold, record retail sales (which start the warranty), and transfer
// stock to other resellers. Gated by useOrg().isReseller; the API also 403s non-resellers.
// Units that haven't passed factory QA (commission-finalize) can't be sold or transferred —
// the API enforces it and the buttons are disabled with a "QA pending" badge.
export function ResellerPage() {
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const { isReseller, isPending: orgPending, name } = useOrg();
  const qc = useQueryClient();

  const stock = useQuery({
    queryKey: ["reseller-stock", orgId],
    queryFn: () => api.resellerStock(orgId!),
    enabled: !!orgId && isReseller,
  });
  const targets = useQuery({
    queryKey: ["reseller-transfer-targets", orgId],
    queryFn: () => api.resellerTransferTargets(orgId!),
    enabled: !!orgId && isReseller,
  });

  const [sellTarget, setSellTarget] = useState<string | null>(null);
  const [soldAt, setSoldAt] = useState("");
  const [contact, setContact] = useState("");
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const [transferToOrg, setTransferToOrg] = useState("");
  const [actionErr, setActionErr] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["reseller-stock", orgId] });

  const sell = useMutation({
    mutationFn: (assetSerial: string) =>
      api.resellerSell(orgId!, assetSerial, {
        ...(soldAt ? { sold_at: new Date(soldAt).toISOString() } : {}),
        ...(contact.trim() ? { sold_to_contact: contact.trim() } : {}),
      }),
    onSuccess: () => { setSellTarget(null); setSoldAt(""); setContact(""); setActionErr(null); refresh(); },
    onError: (e: Error) => setActionErr(e.message),
  });

  const transfer = useMutation({
    mutationFn: (v: { assetSerial: string; toOrgId: string }) =>
      api.resellerTransfer(orgId!, v.assetSerial, { to_org_id: v.toOrgId }),
    onSuccess: () => { setTransferTarget(null); setTransferToOrg(""); setActionErr(null); refresh(); },
    onError: (e: Error) => setActionErr(e.message),
  });

  if (orgPending) return <p className="muted">Loading…</p>;
  if (!isReseller) {
    return (
      <>
        <h1>Reseller</h1>
        <p className="muted">
          This organisation isn't a reseller. The reseller portal (stock, sales, transfers)
          is only available to reseller-type organisations.
        </p>
      </>
    );
  }

  const transferOrgs = targets.data?.orgs ?? [];

  return (
    <>
      <h1>Reseller — stock</h1>
      <p className="muted">
        Units {name ?? "your organisation"} currently holds in stock. Record a sale to start
        the 12-month warranty, or transfer stock to another reseller. Units still awaiting
        factory QA can't be sold or transferred.
      </p>
      {actionErr && <p className="err">{actionErr}</p>}
      {stock.isLoading && <p className="muted">Loading…</p>}
      {stock.error && <p className="err">Couldn't load stock — {(stock.error as Error).message}</p>}
      {stock.data && stock.data.assets.length === 0 && (
        <div className="card"><p className="muted">No units in stock.</p></div>
      )}
      {stock.data && stock.data.assets.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr><th>Asset serial</th><th>CM5 serial</th><th>Commissioned</th><th>QA</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {stock.data.assets.map((a: Asset) => (
                <tr key={a.assetSerial}>
                  <td><code className="code-sm">{a.assetSerial}</code></td>
                  <td><code className="code-xs muted">{a.serial ?? "—"}</code></td>
                  <td className="muted">
                    {a.commissionedAt ? new Date(a.commissionedAt).toLocaleDateString() : "—"}
                  </td>
                  <td>
                    {a.qaPassed
                      ? <span className="pill pass">passed</span>
                      : <span className="pill cancelled" title="Finish factory QA (commission-finalize) to enable sale/transfer">pending</span>}
                  </td>
                  <td>
                    <div className="row">
                      <button
                        disabled={sell.isPending || !a.qaPassed}
                        title={a.qaPassed ? undefined : "QA pending — can't sell yet"}
                        onClick={() => { setSellTarget(a.assetSerial); setTransferTarget(null); setActionErr(null); }}
                      >Record sale</button>
                      <button
                        className="secondary"
                        disabled={transfer.isPending || !a.qaPassed || transferOrgs.length === 0}
                        title={!a.qaPassed ? "QA pending — can't transfer yet"
                          : transferOrgs.length === 0 ? "No other reseller organisations to transfer to"
                          : undefined}
                        onClick={() => {
                          setTransferTarget(a.assetSerial);
                          setSellTarget(null);
                          setTransferToOrg(transferOrgs[0]?.id ?? "");
                          setActionErr(null);
                        }}
                      >Transfer</button>
                    </div>
                    {sellTarget === a.assetSerial && (
                      <form
                        className="formstack"
                        style={{ marginTop: "0.5rem" }}
                        onSubmit={(e) => { e.preventDefault(); sell.mutate(a.assetSerial); }}
                      >
                        <label>Sale date (blank = today)
                          <input type="date" value={soldAt} onChange={(e) => setSoldAt(e.target.value)} />
                        </label>
                        <label>Buyer email/phone (optional)
                          <input
                            type="text"
                            value={contact}
                            placeholder="for warranty confirmation + recall notices"
                            onChange={(e) => setContact(e.target.value)}
                          />
                        </label>
                        <div className="row">
                          <button type="submit" disabled={sell.isPending}>
                            {sell.isPending ? "Recording…" : "Confirm sale"}
                          </button>
                          <button type="button" className="secondary" onClick={() => { setSellTarget(null); setActionErr(null); }}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                    {transferTarget === a.assetSerial && (
                      <form
                        className="formstack"
                        style={{ marginTop: "0.5rem" }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (transferToOrg) transfer.mutate({ assetSerial: a.assetSerial, toOrgId: transferToOrg });
                        }}
                      >
                        <label>Transfer to
                          <select value={transferToOrg} onChange={(e) => setTransferToOrg(e.target.value)}>
                            {transferOrgs.map((o) => (
                              <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                          </select>
                        </label>
                        <div className="row">
                          <button type="submit" disabled={transfer.isPending || !transferToOrg}>
                            {transfer.isPending ? "Transferring…" : "Confirm transfer"}
                          </button>
                          <button type="button" className="secondary" onClick={() => { setTransferTarget(null); setActionErr(null); }}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
